import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Icon,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import { styled } from '@mui/material/styles';
import { DeleteVersion, SaveFotaVersion, UpdateFotaVersionById } from '../../../network/Fota';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: { xs: '70%', md: '40%' },
  maxWidth: { xs: '70%', md: '40%' },
  bgcolor: 'background.paper',
  p: 2,
  borderRadius: 1,
  maxHeight: '90vh',
  overflowY: 'auto'
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
});

export default function AddNewVersion({
  openAssignedModal,
  setIsAssignAddModal,
  setAlertMetas,
  alertMetas,
  selectedFotaData,
  setSelectedFotaData,
  onAction
}) {
  const versionSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    version: Yup.string().required('Required'),
    file: Yup.string().required('Required'),
    details: Yup.string().required('Required')
  });

  const addNewVersionFormik = useFormik({
    enableReinitialize: true, // This enables the form to reset when initialValues change
    initialValues: {
      name: selectedFotaData?.name || '',
      version: selectedFotaData?.version || '',
      file: null,
      details: selectedFotaData?.details || ''
    },
    validationSchema: versionSchema,
    onSubmit: (values, { resetForm }) => {
      createFotaVersion(values);
      resetForm();
    }
  });

  const { errors, touched, handleSubmit, getFieldProps, setFieldValue } = addNewVersionFormik;

  const handleClose = () => {
    setSelectedFotaData(null);
    addNewVersionFormik.resetForm();
    setIsAssignAddModal(false);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFieldValue('file', file); // Store the file object in Formik state
    }
  };

  const createFotaVersion = async (values) => {
    try {
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('version', values.version);
      formData.append('details', values.details);
      if (values.file) {
        formData.append('file', values.file);
      }
      const res = selectedFotaData?.id
        ? await UpdateFotaVersionById(selectedFotaData?.id, formData)
        : await SaveFotaVersion(formData);
      if (res) {
        setAlertMetas({
          ...alertMetas,
          severity: 'success',
          snackBarOpened: true,
          msg: 'New version of FOTA created Successfully !!'
        });
        setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
        onAction();
        handleClose();
      }
    } catch (error) {
      setAlertMetas({
        ...alertMetas,
        severity: 'error',
        snackBarOpened: true,
        msg: error?.response?.data?.validationErrors?.message || 'Something went wrong!'
      });
      setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
      handleClose();
    }
  };

  return (
    <Box>
      <Modal
        open={openAssignedModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        data-testid="add-new-version"
      >
        <FormikProvider value={addNewVersionFormik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Box sx={style}>
              <Typography sx={{ fontSize: { xs: 16, md: 18 }, fontWeight: 900, mb: 3 }}>
                {selectedFotaData?.id ? 'Update Fota Version' : ' Create New Version'}
              </Typography>
              <Box display="flex" flexDirection="column" gap="10px">
                <FormControl fullWidth>
                  <TextField
                    label={
                      <Typography
                        sx={{
                          fontSize: {
                            xs: '12px',
                            sm: '14px'
                          },
                          fontWeight: 500
                        }}
                      >
                        Name *
                      </Typography>
                    }
                    sx={{
                      '& .MuiOutlinedInput-input': {
                        fontSize: { xs: '12px', sm: '14px' },
                        padding: { xs: '14px 8px', sm: '16.5px' }
                      }
                    }}
                    id="name"
                    name="name"
                    autoComplete="off"
                    variant="outlined"
                    {...getFieldProps('name')}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </FormControl>
                <FormControl data-testid="version" fullWidth>
                  <TextField
                    label={
                      <Typography
                        sx={{
                          fontSize: {
                            xs: '12px',
                            sm: '14px'
                          },
                          fontWeight: 500
                        }}
                      >
                        Version *
                      </Typography>
                    }
                    sx={{
                      '& .MuiOutlinedInput-input': {
                        fontSize: { xs: '12px', sm: '14px' },
                        padding: { xs: '14px 8px', sm: '16.5px' }
                      }
                    }}
                    id="version"
                    name="version"
                    autoComplete="off"
                    variant="outlined"
                    {...getFieldProps('version')}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </FormControl>
                <FormControl fullWidth>
                  <Typography sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: 500 }}>
                    Version File *
                  </Typography>
                  <TextField
                    type="file"
                    sx={{ '& .MuiOutlinedInput-input': { fontSize: { xs: '12px', sm: '14px' } } }}
                    id="file"
                    name="file"
                    variant="outlined"
                    onChange={handleFileUpload}
                    inputProps={{ accept: '.bin, .hex, .zip', 'data-testid': 'file-upload-input' }}
                    error={Boolean(touched.file && errors.file)}
                    helperText={touched.file && errors.file}
                  />
                </FormControl>

                {/* <FormControl fullWidth>
                  <TextField
                    label={
                      <Typography
                        sx={{
                          fontSize: {
                            xs: '12px',
                            sm: '14px'
                          },
                          fontWeight: 500
                        }}
                      >
                        Version file *
                        <VisuallyHiddenInput type="file" onChange={handleFileUpload} />
                      </Typography>
                    }
                    sx={{
                      '& .MuiOutlinedInput-input': {
                        fontSize: { xs: '12px', sm: '14px' },
                        padding: { xs: '14px 8px', sm: '16.5px' }
                      }
                    }}
                    id="name"
                    name="name"
                    autoComplete="off"
                    variant="outlined"
                    {...getFieldProps('name')}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </FormControl> */}
                <FormControl fullWidth>
                  <TextField
                    label={
                      <Typography
                        sx={{
                          fontSize: {
                            xs: '12px',
                            sm: '14px'
                          },
                          fontWeight: 500
                        }}
                      >
                        About version *
                      </Typography>
                    }
                    sx={{
                      '& .MuiOutlinedInput-input': {
                        fontSize: { xs: '12px', sm: '14px' },
                        padding: { xs: '14px 8px', sm: '16.5px' }
                      }
                    }}
                    id="details"
                    name="details"
                    autoComplete="off"
                    variant="outlined"
                    {...getFieldProps('details')}
                    multiline
                    minRows={6}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </FormControl>
                <Box
                  sx={{
                    display: 'flex',
                    gap: '10px',
                    justifyContent: 'flex-end',
                    flexWrap: 'wrap'
                  }}
                >
                  <Button variant="outlined" size="small" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button type="submit" size="small" variant="contained">
                    {selectedFotaData?.id ? 'Save Changes' : 'Add'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Form>
        </FormikProvider>
      </Modal>
    </Box>
  );
}
